/**
 * SAB mailbox protocol shared by the kernel worker, cpu workers, and the
 * Node test harness.
 *
 * Layout within the kernel-reserved SAB region:
 * - offsets `0x0000-0x0FFF`: global control words like `WAKE_SLOT`
 * - offsets `0x1000..`: fixed-size per-process syscall mailboxes
 * - after the mailbox slots: one shared memory-RPC control block
 * - after the RPC block: bulk transfer buffer for cross-worker memory copies
 *
 * The mailbox slots are indexed by HAL cpu-worker id, not kernel PID. PID 0 is
 * reserved for the kernel; valid process slots are `1..MAX_PROC - 1`.
 */

// Max processes (must match kernel/src/proc/pcb.rs MAX_PROCESSES)
export const MAX_PROC = 64;

// Global worker wake slot in the SAB control region.
export const WAKE_SLOT = 0;
export const SAB_VERSION_SLOT = 3;
export const SAB_LAYOUT_VERSION = 2;

// Per-process mailbox size in bytes.
export const MBOX_SLOT_SIZE = 128;

// Mailbox region starts at 4 KiB into the kernel-reserved SAB area.
export const MBOX_BASE = 0x1000;

// Memory-access RPC control block follows the mailbox slots.
export const MEM_RPC_BASE = MBOX_BASE + MAX_PROC * MBOX_SLOT_SIZE;

// Shared data buffer for bulk cross-worker memory copies.
export const DATA_BASE = MEM_RPC_BASE + 128;
export const DATA_SIZE = 256 * 1024;

export const FLAG_IDLE = 0;
export const FLAG_SYSCALL_PENDING = 1;
export const FLAG_RESULT_READY = 2;
export const FLAG_MEM_READ_REQ = 3;
export const FLAG_MEM_WRITE_REQ = 4;
export const FLAG_MEM_DONE = 5;
export const FLAG_MEM_SIZE_REQ = 6;
export const FLAG_MEM_GROW_REQ = 7;
export const FLAG_MEM_GROW_FAILED = 8;

// Per-process mailbox field offsets, relative to the slot base.
const F_REQUEST_FLAG = 0;
const F_SYSCALL_NR   = 4;
const F_ARG0         = 8;
const F_ARG1         = 12;
const F_ARG2         = 16;
const F_ARG3         = 20;
const F_ARG4         = 24;
const F_ARG5         = 28;
export const F_RESULT_LO    = 32;
const F_RESULT_HI    = 36;
const F_PID          = 40;

// Memory-RPC field offsets, relative to MEM_RPC_BASE.
const M_REQUEST_FLAG = 0;
const M_TARGET_PID   = 4;
const M_OFFSET       = 8;
const M_LENGTH       = 12;
const M_DATA_OFFSET  = 16;

// PID 0 is unused; process slots start at 1.
export function slotOffset(pid) {
    return MBOX_BASE + pid * MBOX_SLOT_SIZE;
}

// Int32Array index for Atomics access to the slot request flag.
export function flagIndex(pid) {
    return (MBOX_BASE + pid * MBOX_SLOT_SIZE + F_REQUEST_FLAG) >> 2;
}

export function writeSyscall(i32View, pid, nr, a0, a1, a2, a3, a4, a5) {
    const base = (slotOffset(pid)) >> 2; // i32 index
    i32View[base + (F_SYSCALL_NR >> 2)]  = nr;
    i32View[base + (F_ARG0 >> 2)]        = a0;
    i32View[base + (F_ARG1 >> 2)]        = a1;
    i32View[base + (F_ARG2 >> 2)]        = a2;
    i32View[base + (F_ARG3 >> 2)]        = a3;
    i32View[base + (F_ARG4 >> 2)]        = a4;
    i32View[base + (F_ARG5 >> 2)]        = a5;
    i32View[base + (F_PID >> 2)]         = pid;
}

export function readSyscall(i32View, pid) {
    const base = (slotOffset(pid)) >> 2;
    return {
        nr:   i32View[base + (F_SYSCALL_NR >> 2)],
        args: [
            i32View[base + (F_ARG0 >> 2)],
            i32View[base + (F_ARG1 >> 2)],
            i32View[base + (F_ARG2 >> 2)],
            i32View[base + (F_ARG3 >> 2)],
            i32View[base + (F_ARG4 >> 2)],
            i32View[base + (F_ARG5 >> 2)],
        ],
        pid:  i32View[base + (F_PID >> 2)],
    };
}

export function writeResult(i32View, pid, resultI64) {
    const base = (slotOffset(pid)) >> 2;
    i32View[base + (F_RESULT_LO >> 2)] = Number(resultI64 & 0xFFFFFFFFn);
    i32View[base + (F_RESULT_HI >> 2)] = Number((resultI64 >> 32n) & 0xFFFFFFFFn);
}

export function readResult(i32View, pid) {
    const base = (slotOffset(pid)) >> 2;
    const lo = i32View[base + (F_RESULT_LO >> 2)] >>> 0; // unsigned
    const hi = i32View[base + (F_RESULT_HI >> 2)] >>> 0;
    return BigInt(lo) | (BigInt(hi) << 32n);
}

export function memRpcFlagIndex() {
    return (MEM_RPC_BASE + M_REQUEST_FLAG) >> 2;
}

export function writeMemRequest(i32View, flag, targetPid, offset, length, dataOffset) {
    const base = MEM_RPC_BASE >> 2;
    i32View[base + (M_TARGET_PID >> 2)]   = targetPid;
    i32View[base + (M_OFFSET >> 2)]       = offset;
    i32View[base + (M_LENGTH >> 2)]       = length;
    i32View[base + (M_DATA_OFFSET >> 2)]  = dataOffset;
    // Set flag last so readers only observe a fully populated request.
    Atomics.store(i32View, base + (M_REQUEST_FLAG >> 2), flag);
}

export function readMemRequest(i32View) {
    const base = MEM_RPC_BASE >> 2;
    return {
        flag:       Atomics.load(i32View, base + (M_REQUEST_FLAG >> 2)),
        targetPid:  i32View[base + (M_TARGET_PID >> 2)],
        offset:     i32View[base + (M_OFFSET >> 2)],
        length:     i32View[base + (M_LENGTH >> 2)],
        dataOffset: i32View[base + (M_DATA_OFFSET >> 2)],
    };
}

export function writeMemSizeResult(i32View, byteLength) {
    const base = MEM_RPC_BASE >> 2;
    i32View[base + (M_OFFSET >> 2)] = byteLength >>> 0;
}

export function readMemSizeResult(i32View) {
    const base = MEM_RPC_BASE >> 2;
    return i32View[base + (M_OFFSET >> 2)] >>> 0;
}
